"use client";
import { useState } from "react";
import { MapPin, Mail, Phone } from "lucide-react";
import { z } from "zod";

// Ui components
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

import { useFetchSWR } from "@/lib/swrConfig";
import { ParentCompanyData } from "@/types/companies";
import endPoints from "@/lib/apiEndPoints";

import { Separator } from "@/components/ui/separator";
import HeaderTitle from "@/components/reusebale-components/HeaderTitle";
import api from "@/lib/api";
import { ContactData, ContactRes } from "@/types/contact";

// ---------------------- ZOD SCHEMA ----------------------
const contactSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Enter a valid email"),
  phone: z
    .string()
    .min(10, "Phone number is required")
    .regex(/^[0-9+\- ]+$/, "Invalid phone number"),
  message: z.string().min(5, "Message must be at least 5 characters"),
});
// ---------------------------------------------------------

export default function ContactPage() {
  const submitContactForm = async (
    data: ContactData
  ): Promise<ContactRes> => {
    const response = await api.post(endPoints.CONTACT, data);
    return response.data;
  };

  // get parrent company details
  const { data: comRes } = useFetchSWR(endPoints.PARRENT_COMPANY);
  const company: ParentCompanyData =
    comRes?.status === 200 ? comRes.data : {};

  const [submiting, setSubmiting] = useState(false);
  const [resMessage, setResMessage] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<ContactData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });

    // Clear error when typing
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmiting(true);

    const validation = contactSchema.safeParse(form);

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.issues.forEach((issue) => {
        fieldErrors[String(issue.path[0])] = issue.message;
      });

      setErrors(fieldErrors);
      setSubmiting(false);
      return;
    }

    try {
      const res = await submitContactForm(form);
      if (res.status === 200) {
        setResMessage(res.message || "Form submitted successfully");
        setForm({ name: "", email: "", phone: "", message: "" });
        setErrors({});
      } else {
        setResMessage(
          res.message || "Unable to submit form please try again"
        );
      }
    } catch (err) {
      setResMessage("Something went wrong. Please try again later.");
    } finally {
      setSubmiting(false);
    }
  };

  return (
    <div className="container mt-6 md:mt-10 lg:mt-16">
      <HeaderTitle title="Get in Touch" url="" />

      <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4 md:gap-8 lg:gap-16 ">
        {/* Contact Form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            {/* Name */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-base">
                Name
              </Label>
              <Input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Full Name"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-base">
                Email
              </Label>
              <Input
                type="text"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="example@gmail.com"
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone" className="text-base">
                Phone
              </Label>
              <Input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+880 1700000000"
              />
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Message */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="message" className="text-base">
                Message
              </Label>
              <Textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Short Message"
              />
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="mt-4 md:mt-6 py-2 px-6 rounded-full bg-primary hover:bg-prime-hover"
            disabled={submiting}
          >
            {submiting ? (
              <div className="flex items-center gap-2">
                <Spinner />
                <span>Submitting...</span>
              </div>
            ) : (
              "Submit"
            )}
          </Button>

          {resMessage && (
            <p className="my-2 text-sm text-muted-foreground">
              {resMessage}
            </p>
          )}
        </form>

        {/* Map Image */}
        <Card className="overflow-hidden p-0 aspect-3/2">
          <iframe
            src={
              company.map_url ||
              "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d116833.95338886735!2d90.419689!3d23.780840500000014"
            }
            title="map"
            width="100%"
            height="100%"
            loading="eager"
          ></iframe>
        </Card>
      </div>

      <Separator className="my-6 md:my-10 lg:my-16 bg-primary" />

      {/* Remaining sections unchanged */}
      {/* ---------------------------------- */}

      <div className="pb-6 md:pb-8 lg:pb-16 ">
        <Card className="overflow-hidden p-0 shadow-none">
          <div className="grid grid-cols-2 lg:grid-cols-3">
            {/* image column*/}
            <div className="flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <Image
                src="mail.png"
                alt=""
                width={200}
                height={150}
                className="w-full h-full object-fit bg-white"
              />
            </div>

            {/* address */}
            <div className="order-3 lg:order-2 flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <div className="flex flex-col items-center justify-center text-center">
                <MapPin className="w-8 h-8 mb-4" strokeWidth={1.5} />
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold mb-3">
                  Address
                </h3>
                <p className="text-xs md:text-sm">{company?.address}</p>
              </div>
            </div>

            {/* image */}
            <div className="order-5 lg:order-3 flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <Image
                src="male_avatar.png"
                alt=""
                width={200}
                height={150}
                className="w-full h-full object-fit bg-white"
              />
            </div>

            {/* email */}
            <div className="order-2 lg:order-4 flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <div className="flex flex-col items-center justify-center text-center">
                <Mail className="w-8 h-8 mb-4" strokeWidth={1.5} />
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold mb-3">
                  Email
                </h3>
                {company.emails?.map((email, index) => (
                  <p key={index} className="text-xs md:text-sm">
                    {email}
                  </p>
                ))}
              </div>
            </div>

            {/* image */}
            <div className="order-4 lg:order-5 flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <Image
                src="map.png"
                alt=""
                width={200}
                height={150}
                className="w-full h-full object-fit bg-white"
              />
            </div>

            {/* phone */}
            <div className="order-6 flex items-center justify-center min-h-[200px] md:h-[400px]  bg-primary text-white">
              <div className="flex flex-col items-center justify-center text-center">
                <Phone className="w-8 h-8 mb-4" strokeWidth={1.5} />
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold mb-3">
                  Phone
                </h3>
                {company.phones?.map((phone, index) => (
                  <p key={index} className="text-xs md:text-sm">
                    {phone}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
